/**
 * The landing page's bands around the lab map. Before it, a short answer to the three things a
 * skeptical visitor asks first: who reads the labs, what is in the powder and why, and who sees
 * them. After it, the map already shows who is involved, what goes in and
 * why, and how a formula changes with new labs, so what is left is what it does not: what happens
 * when a visitor's results look different from Jordan's, and what it costs and when they pay.
 *
 * Both bands share one grid, a 5fr column for the heading and a 7fr column for the content, so
 * their right-hand columns start on the same vertical line. The price panel echoes the hero and
 * ends the page on the upload button.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { dark } from "@lattice/tokens/themes";
import { UploadButton } from "./Upload.tsx";
const WIDE = "@media (min-width: 960px)";
const MID = "@media (min-width: 720px)";
const GUTTER = "clamp(20px, 4vw, 56px)";

/** The page frame around the dark panels, so their contents line up with the gutter. */
const FRAME = "8px";
const SPLIT_GAP = "clamp(40px, 6vw, 96px)";

/** U+2011, so "add‑on" never breaks across a line on a narrow screen. */
const NB = "‑";
const styles = {
  price: {
    ks0D6T: "x18zekx4",
    kUOVxO: "xvueqy4",
    kaIpWk: "x2u8bby x1h6wx5f",
    krdFHd: "x8k982a",
    kfmiAY: "x1ykue90",
    kWkggS: "x1l0w1cl",
    kMwMTN: "xzb3lse",
    kg3NbH: "xelt9ym x1xtxxwa",
    k8WAf4: "xzydexm",
    k1xSpc: "xrvj5dj",
    kumcoG: "x1y6fwsi x1sx0k03",
    k1C7PZ: "xtmfs0b",
    khm7nJ: "xbnhjcb",
    kGNEyG: "x7a106z",
    $$css: true
  }
};
const NOTES = [{
  title: "If your labs are all in range",
  body: "We tell you so and add nothing for them. Protein and creatine are optional."
}, {
  title: "Take out what you don't want",
  body: "Remove any ingredient except the protein base before a physician signs."
}, {
  title: "When you retest",
  body: "Each month we ask about new labs. Upload them any time and a physician signs a new version."
}] as const;
const BEFORE = [{
  title: "A physician reviews your labs",
  body: "You pay only after they approve it. They can adjust or decline the formula, and they do it within 24 hours."
}, {
  title: "What is in it, and why",
  body: "Protein, creatine, and the vitamins and minerals your results call for, each with the reason behind it."
}, {
  title: "Who sees your labs",
  body: "Your physician sees them to review your formula. We use them to draft it, and we do not sell them."
}] as const;

/** The first objections a skeptical visitor has, answered before the long example. */
export function Before() {
  return <section aria-labelledby="before-heading" className="x18zekx4 xvueqy4 xelt9ym xgs5mw1">
      <h2 id="before-heading" className="x1ghz6dp x154s7bo xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">
        Before you upload
      </h2>
      <ul className="xe8uvvx x9nozs9 xrxpjvj x1717udv xrvj5dj x1y6fwsi xi3rd2o x1iojj6j">
        {BEFORE.map(item => <li key={item.title} className="x78zum5 xdt5ytf xskm3c9 x1wiri7k xez8t42 x13fuv20 x153ygsw">
            <h3 className="x1ghz6dp x11mdwsw xqu410z x1e5flnj x1w2vvpw">{item.title}</h3>
            <p className="x1ghz6dp x1jty25b xbz0q3o x1fzhlzt">{item.body}</p>
          </li>)}
      </ul>
    </section>;
}
export function Notes() {
  return <section id="questions" aria-labelledby="questions-heading" className="x18zekx4 xvueqy4 xelt9ym xgs5mw1">
      <div className="xrvj5dj x1y6fwsi x1sx0k03 xtmfs0b xbnhjcb x7a106z">
        <h2 id="questions-heading" className="x1ghz6dp x154s7bo xqu410z x1ntj4s9 x1d3j7mx x1e5flnj x1w2vvpw">
          What if my labs look different?
        </h2>
        <div>
          <ul className="xe8uvvx x1ghz6dp x1717udv">
            {NOTES.map(note => <li key={note.title} className="xrvj5dj x1y6fwsi x6tquyw x1iojj6j xcfm6fe x1wiri7k xez8t42 x13fuv20 x153ygsw">
                <h3 className="x1ghz6dp x11mdwsw xqu410z x1e5flnj x1w2vvpw">{note.title}</h3>
                <p className="x1ghz6dp x1jty25b xbz0q3o x1fzhlzt">{note.body}</p>
              </li>)}
          </ul>
          <div className="xeonxcd">
            <UploadButton label="Upload your labs" />
          </div>
        </div>
      </div>
    </section>;
}
export function Price() {
  return <div className="xnjsko4 xrt8cxi x18d9i69 x1delj98 xr24p64" id="pricing">
      <section {...stylex.props(dark, styles.price)} aria-labelledby="pricing-heading">
        <div className="x78zum5 xdt5ytf x626il7">
          <h2 id="pricing-heading" className="x1ghz6dp x1u57udm xqu410z x1ntj4s9 xu01rmg x14abtgu x1l1vl4p">
            $175 <span className="x3cmmws x12oo3zp x1jty25b xgvszs4">a month</span>
          </h2>
          <p className="x1ghz6dp x11mdwsw xowlazn x1fzhlzt">
            Charged when you check out, after a physician approves your formula.
          </p>
        </div>
        <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl">
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf x1173whf x1rf3zrh xm3hrcx">
            <li>A physician reviews your labs and every retest</li>
            <li>A new pouch every month, mixed to order</li>
            <li>Pause, skip a month or cancel from your account</li>
          </ul>
          <p className="x1ghz6dp x1jty25b x19pysoa x1fzhlzt">
            The omega{NB}3 add{NB}on is $5 more a month.
          </p>
          <UploadButton label="Upload your labs" />
        </div>
      </section>
    </div>;
}